'use client';
import SplitFlap from './SplitFlap';
import { roundNameShort } from './playoffs';

/* ============================================================
   BoardHeader — the board's title line.

   League name as an amber split-flap marquee, then a status flap:

     [LEAGUE NAME]  [WEEK 15]  [Q-FINAL]

   THE STATUS FLAP is time; the ROUND FLAP is stakes.

   WEEK vs FINAL -- the thing that confused us once, documented so it
   doesn't again:

     `inSeason` comes from /state/nfl, and it means "is the NFL season
     running RIGHT NOW", NOT "did this league finish". Off-season
     (nflState.week === 0, i.e. Feb-Aug) it is ALWAYS false, so the header
     always read FINAL and the week number was never visible in summer
     testing. That was correct behavior being mistaken for a bug: in
     December the same code shows WEEK 15.

   But FINAL alone was genuinely thin -- it said the season ended without
   saying what ended it. So off-season we now show the round too:

     WEEK 12                 regular season, in season
     WEEK 15   Q-FINAL       playoff week, in season
     WEEK 17   'SHIP         championship week, in season
     FINAL     'SHIP         off-season, league finished on its title game
     FINAL                   off-season, no playoff data

   ROUND LABEL IS BACK IN THE HEADER (reversing the earlier decision).
   The original argument was that round 2 of a 6-team bracket holds a
   semifinal AND a 5th-place game, so one header label lies. That is still
   true -- which is why the header shows the round of the BRACKET
   (`playoff.roundLabel`, derived from the round number) while individual
   rows carry their own placement via the medal marker. The header answers
   "where are we in the playoffs", not "what is every row".

   The row-level SplitFlap labels are GONE. They pushed the status flip-dot
   off-screen on real boards. Rows now carry a medal glyph instead (see
   MatchupRow) -- one character where nine cells used to be.

   NAME WIDTH: the flap sizes to CONTENT (clamped 14..max) rather than
   reserving a fixed 18. Most league names are 10-16 chars, so a fixed
   width left dead flaps on nearly every board.

   Props:
     leagueName  string
     week        number|string
     inSeason    bool
     playoff     null | { round, totalRounds, roundLabel }
     size        px font-size for the flaps
     nameWidth   MAX cells for the name (default 28; floor of 14)
   ============================================================ */

const AMBER = '#ffb300';
const DIM = '#5c6068';

const NAME_MIN = 14;
const ROUND_CELLS = 7;   // fits Q-FINAL, SEMFNAL, ROUND 4, 'SHIP

// Size the name flap to its content, between a floor and the caller's
// ceiling. The floor keeps short names from collapsing into a stub; the
// ceiling is the layout budget for the tier.
export function fitNameWidth(name, max = 28, min = NAME_MIN) {
  const len = String(name || '').trim().length;
  const ceiling = Math.max(min, max);
  return Math.min(ceiling, Math.max(min, len));
}

// Time. Off-season a week number is a lie -- the league isn't ON week 17,
// it FINISHED on week 17.
//
// EXCEPT when the round flap already says the season is over. `FINAL`
// next to `'SHIP` is the same fact twice, and two amber flaps of nearly
// equal length read as one run-on label. When the board is parked on a
// finished championship, the round flap carries it alone and this one
// shows the week that produced it -- which is the more useful number and
// the one that was previously invisible off-season.
export function headerStatusText(week, inSeason, playoff = null) {
  if (inSeason) return `WEEK ${week}`;
  const parkedOnFinal = playoff && playoff.round === playoff.totalRounds;
  return parkedOnFinal ? `WEEK ${week}` : 'FINAL';
}

// Stakes. Empty string outside the playoffs, which renders as a blank flap
// that we simply don't mount.
export function headerRoundText(playoff) {
  if (!playoff) return '';
  return roundNameShort(playoff.round, playoff.totalRounds);
}

export default function BoardHeader({
  leagueName = '',
  week = '',
  size = 26,
  nameWidth = 28,
  inSeason = true,
  playoff = null,
}) {
  const statusText = headerStatusText(week, inSeason, playoff);
  const roundText = headerRoundText(playoff);
  const fitted = fitNameWidth(leagueName, nameWidth);

  return (
    <div
      className="board-header"
      style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 26 }}
    >
      <SplitFlap
        text={leagueName}
        size={size}
        width={fitted}
        align="left"
        color={AMBER}
        className="bh-name"
      />
      <SplitFlap
        text={statusText}
        size={size}
        width={ROUND_CELLS}
        align="left"
        color={AMBER}
        className="bh-week"
      />
      {/* Only mounted during the playoffs, so 14 weeks of the year the
          header is exactly as wide as it always was.

          DIM, not amber. Three equal-brightness amber flaps in a row read
          as one long label -- which is exactly how "MIKE DITKA BUTKIS
          FINAL 'SHIP" looked. Dropping this one to grey re-establishes
          the hierarchy: name, then when, then context. */}
      {roundText ? (
        <SplitFlap
          text={roundText}
          size={size}
          width={ROUND_CELLS}
          align="left"
          color={DIM}
          className="bh-round"
        />
      ) : null}
    </div>
  );
}
